import { useCallback, useMemo, useState } from "react";

/**
 * Dummy quiz payload shaped like ai_v2.md (`data.questions`).
 * Swap for API data later without changing the UI.
 */
export const DUMMY_QUIZ = {
  message_id: "msg_quiz_dummy",
  conversation_id: "conv_dummy",
  action_type: "quiz",
  quiz_type: "single_choice",
  questions: [
    {
      question_id: "q_001",
      question_text:
        "According to the communication style guide, which personality type requires emotional safety and tone awareness, necessitating that a leader acknowledges feelings before facts? [[148]]",
      options: [
        { option_id: "a", text: "The Empath" },
        { option_id: "b", text: "The Collaborator" },
        { option_id: "c", text: "The Activator" },
        { option_id: "d", text: "The Analyst" },
      ],
      correct_answer: "a",
      explanation:
        "The Empath needs emotional safety first. Acknowledging feelings before facts helps them stay open to the message.",
      difficulty: "intermediate",
    },
    {
      question_id: "q_002",
      question_text:
        "What is the main risk when leaders over-automate internal communication with AI tools?",
      options: [
        { option_id: "a", text: "Messages become too short" },
        {
          option_id: "b",
          text: "Communication sounds generic and loses authenticity",
        },
        { option_id: "c", text: "Teams receive fewer emails" },
        { option_id: "d", text: "Meetings are cancelled automatically" },
      ],
      correct_answer: "b",
      explanation:
        "Over-automation can make messages feel generic. Human judgment keeps communication authentic and trustworthy.",
      difficulty: "intermediate",
    },
    {
      question_id: "q_003",
      question_text:
        "Which practice best pairs AI assistance with human leadership?",
      options: [
        { option_id: "a", text: "Let AI send every message without review" },
        { option_id: "b", text: "Use AI only for formatting, never for drafts" },
        {
          option_id: "c",
          text: "Use AI to prepare and triage, while keeping sensitive conversations human",
        },
        { option_id: "d", text: "Disable AI for all internal channels" },
      ],
      correct_answer: "c",
      explanation:
        "AI works best for preparation and triage. High-stakes or sensitive conversations should stay distinctly human.",
      difficulty: "intermediate",
    },
    {
      question_id: "q_004",
      question_text:
        "In the Leader Amplification Effect, why do visible leaders need extra care with tone?",
      options: [
        {
          option_id: "a",
          text: "Because their messages are never read carefully",
        },
        {
          option_id: "b",
          text: "Because every message they send is multiplied across the organization",
        },
        { option_id: "c", text: "Because AI cannot detect tone at all" },
        { option_id: "d", text: "Because teams prefer emoji-only updates" },
      ],
      correct_answer: "b",
      explanation:
        "Visible leaders amplify every signal — clarity and tone matter more because impact spreads widely.",
      difficulty: "beginner",
    },
    {
      question_id: "q_005",
      question_text:
        "Which outcome best describes successful AI-supported internal communication?",
      options: [
        { option_id: "a", text: "More messages sent with no editorial review" },
        {
          option_id: "b",
          text: "Faster preparation with authenticity, empathy, and accountability retained",
        },
        { option_id: "c", text: "Replacing all managers with chatbots" },
        { option_id: "d", text: "Removing citations from shared updates" },
      ],
      correct_answer: "b",
      explanation:
        "Success is a balance: AI speeds preparation while people keep authenticity, empathy, and accountability.",
      difficulty: "intermediate",
    },
  ],
};

export const getQuizFeedbackKey = (correct, total) => {
  if (!total) return "keepPracticing";
  const ratio = correct / total;
  if (ratio >= 0.8) return "greatJob";
  if (ratio >= 0.5) return "goodEffort";
  return "keepPracticing";
};

/**
 * Session state for a single-choice quiz (API-shaped questions).
 */
const useQuizSession = (questions = []) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedByQuestion, setSelectedByQuestion] = useState({});
  const [checkedByQuestion, setCheckedByQuestion] = useState({});

  const total = questions.length;
  const currentQuestion = questions[currentIndex] || null;
  const questionId = currentQuestion?.question_id;
  const selectedOptionId = questionId
    ? selectedByQuestion[questionId] ?? null
    : null;
  const isChecked = questionId ? !!checkedByQuestion[questionId] : false;

  const selectOption = useCallback(
    (optionId) => {
      if (!questionId || isChecked) return;
      setSelectedByQuestion((prev) => ({ ...prev, [questionId]: optionId }));
    },
    [questionId, isChecked],
  );

  const checkAnswer = useCallback(() => {
    if (!questionId || !selectedOptionId || isChecked) return;
    setCheckedByQuestion((prev) => ({ ...prev, [questionId]: true }));
  }, [questionId, selectedOptionId, isChecked]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((index) => Math.min(total - 1, index + 1));
  }, [total]);

  const resetSession = useCallback(() => {
    setCurrentIndex(0);
    setSelectedByQuestion({});
    setCheckedByQuestion({});
  }, []);

  const optionState = useMemo(() => {
    if (!currentQuestion) return {};

    const correctId = currentQuestion.correct_answer;
    const states = {};

    currentQuestion.options.forEach((option) => {
      const isSelected = option.option_id === selectedOptionId;
      const isCorrect = option.option_id === correctId;

      if (!isChecked) {
        states[option.option_id] = isSelected ? "selected" : "default";
        return;
      }

      if (isCorrect) {
        states[option.option_id] = "correct";
      } else if (isSelected) {
        states[option.option_id] = "incorrect";
      } else {
        states[option.option_id] = "default";
      }
    });

    return states;
  }, [currentQuestion, selectedOptionId, isChecked]);

  const answers = useMemo(
    () =>
      questions.map((question) => ({
        question_id: question.question_id,
        user_answer: selectedByQuestion[question.question_id] ?? null,
      })),
    [questions, selectedByQuestion],
  );

  const score = useMemo(() => {
    let correct = 0;
    questions.forEach((question) => {
      if (selectedByQuestion[question.question_id] === question.correct_answer) {
        correct += 1;
      }
    });
    return {
      correct,
      total,
      percent: total ? Math.round((correct / total) * 100) : 0,
      feedbackKey: getQuizFeedbackKey(correct, total),
      answers,
    };
  }, [questions, selectedByQuestion, total, answers]);

  return {
    currentIndex,
    total,
    currentQuestion,
    selectedOptionId,
    isChecked,
    canCheck: Boolean(selectedOptionId) && !isChecked,
    canGoPrevious: currentIndex > 0,
    canGoNext: currentIndex < total - 1,
    isLastQuestion: currentIndex === total - 1 && total > 0,
    optionState,
    score,
    answers,
    selectOption,
    checkAnswer,
    goToPrevious,
    goToNext,
    resetSession,
  };
};

export default useQuizSession;
